/**
 * Header — the top bar of the offer viewer. Two layouts, chosen by `isMobile`:
 *   - Desktop: a single white pill with the agency badge, phone and website
 *     (Figma `71:21145`).
 *   - Mobile: an agency row whose chevron reveals the phone number
 *     (Figma `71:17647`).
 *
 * Props (Header):
 *   - isMobile {boolean}
 *   - agency {{ name, email, phone, website, logo }}
 */
import React, { useState } from 'react';
import { useStrings } from './i18n';
import { IconChevronDown, IconPhone, IconMail, IconGlobe, IconFacebook, IconInstagram, IconLinkedIn, IconTwitterX } from './icons';
import { telHref, mailtoHref, websiteHref, EXTERNAL_LINK } from './contactLinks';
import AgencyBadge from './AgencyBadge';

export default function Header({ isMobile, agency }) {
    const t = useStrings();
    const [showPhone, setShowPhone] = useState(false);

    // Optional agency social links (only those set in the viewer settings).
    const social = agency.social || {};
    const socialLinks = [
        ['facebook', IconFacebook, 'Facebook'],
        ['instagram', IconInstagram, 'Instagram'],
        ['linkedin', IconLinkedIn, 'LinkedIn'],
        ['x', IconTwitterX, 'X'],
    ].filter(([key]) => social[key]);
    const socialRow = socialLinks.length > 0 && (
        <div className="rs-header__social">
            {socialLinks.map(([key, Icon, label]) => (
                <a key={key} className="rs-social-link" href={social[key]} aria-label={label} {...EXTERNAL_LINK}>
                    <Icon size={20} />
                </a>
            ))}
        </div>
    );

    // Mobile frame 71:17647 — "Sent on" is its own full-width bar, the agency
    // row carries a chevron that reveals the phone number.
    if (isMobile) {
        return (
            <header className="rs-mheader">
                <div className="rs-mheader__agency">
                    <AgencyBadge agency={agency} />
                    <button
                        className={`rs-mheader__toggle ${showPhone ? 'is-open' : ''}`}
                        onClick={() => setShowPhone((v) => !v)}
                        aria-label={t.toggleContact}
                        aria-expanded={showPhone}
                    >
                        <IconChevronDown />
                    </button>
                </div>
                {showPhone && agency.phone && (
                    <a className="rs-mheader__contact" href={telHref(agency.phone)}>
                        <IconPhone /> <span>{agency.phone}</span>
                    </a>
                )}
                {showPhone && agency.email && (
                    <a className="rs-mheader__contact" href={mailtoHref(agency.email)}>
                        <IconMail /> <span>{agency.email}</span>
                    </a>
                )}
                {showPhone && agency.website && (
                    <a className="rs-mheader__contact" href={websiteHref(agency.website)} {...EXTERNAL_LINK}>
                        <IconGlobe /> <span>{agency.website}</span>
                    </a>
                )}
                {showPhone && socialRow}
            </header>
        );
    }

    return (
        <header className="rs-header">
            <div className="rs-header__inner">
                <img className="rs-header__logo" src={agency.logo} alt={agency.name} />
                <div className="rs-header__row">
                    <p className="rs-header__agency-name">{agency.name}</p>
                    <div className="rs-header__contacts">
                        {agency.phone && (
                            <a className="rs-contact-link" href={telHref(agency.phone)}>
                                <IconPhone size={15} /> <span>{agency.phone}</span>
                            </a>
                        )}
                        {agency.email && (
                            <a className="rs-contact-link" href={mailtoHref(agency.email)}>
                                <IconMail size={15} /> <span>{agency.email}</span>
                            </a>
                        )}
                        {agency.website && (
                            <a className="rs-contact-link" href={websiteHref(agency.website)} {...EXTERNAL_LINK}>
                                <IconGlobe size={15} /> <span>{agency.website}</span>
                            </a>
                        )}
                        {/* socials live in the footer on desktop, not the header row */}
                    </div>
                </div>
            </div>
        </header>
    );
}
